// @ts-nocheck
'use client'

import { useState, useTransition, useEffect } from 'react'
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import _ from 'lodash';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Calendar as CalendarIcon, Download, CreditCard } from 'lucide-react'
import { EmptyState } from '@/components/Page/EmptyState'
import { format } from 'date-fns'
import { cn } from "@/lib/utils"
import getRegistrationName from '@staty/shared/utils/getRegistrationName';
import getTeamDisplayName from '@staty/shared/utils/getTeamDisplayName';
import helpers from '@/utils/helpers';
import usePaymentStore from '@/store/paymentStore';
import useRegistrationStore from '@store/registrationStore';
import { useAuth } from '@/auth/clerkShim';
import { useOutletContext } from 'react-router-dom';

export default function PaymentsList() {
  const [searchTerm, setSearchTerm] = useState('')
  const [registration, setRegistration] = useState('')
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const { getToken } = useAuth();
  const { leagueName = '' } = useOutletContext() as any;
  // const [payments, setPayments] = useState([])
  const [isPending, startTransition] = useTransition()

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const { payments, getPaymentsByLeagueName, refundPaymentByPaymentIntentId } = usePaymentStore((state: any) => ({
    payments: state.payments,
    getPaymentsByLeagueName: state.getPaymentsByLeagueName,
    refundPaymentByPaymentIntentId: state.refundPaymentByPaymentIntentId(getToken),
  }));

  // Refund dialog + in-flight tracking (keyed by payment intent). refundAmountInput
  // holds the (editable) refund amount in dollars — defaults to the full remaining.
  const [confirmTarget, setConfirmTarget] = useState<any>(null);
  const [refundAmountInput, setRefundAmountInput] = useState('');
  const [refundingPI, setRefundingPI] = useState<string | null>(null);
  const { registrations, getAllRegistrations } = useRegistrationStore((state: any) => ({
    registrations: state.registrations,
    getAllRegistrations: state.getAllRegistrations,
  }));

  const getHighlightedText = (text = '', highlight = '') => {
    if (!highlight.trim()) {
      return <span>{text}</span>;
    }
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return <span>{parts.map((part ='', i) => 
      part.toLowerCase() === highlight.toLowerCase() ? <b key={i}>{part}</b> : part
    )}</span>;
  };


  console.log('getPaymentsByLeagueName', payments);

  // Resolve the displayed team name: auto-generated (zqtm_) names → player names
  // via the shared helper (the populated registrationTeam carries teamName +
  // embedded players). Custom names pass through; donations with no team → ''.
  const resolveTeamName = (payment) => {
    const teamName = _.get(payment, 'registrationTeam.teamName') || _.get(payment, 'metadata.teamName') || '';
    if (!teamName) return '';
    return getTeamDisplayName({ teamName, players: _.get(payment, 'registrationTeam.players') || [] });
  };

  // Who actually paid: prefer the Stripe checkout customer_details captured on
  // the Payment (payerName/payerEmail); then the resolved metadata.paidByUser
  // (paidByName/paidByEmail — the player who clicked pay, backfilled server-side
  // for older rows) + metadata.playerEmail; finally the team owner (`user`).
  const payerName = (payment) => _.get(payment, 'payerName') || _.get(payment, 'paidByName') || _.get(payment, 'user.name') || '';
  const payerEmail = (payment) => _.get(payment, 'payerEmail') || _.get(payment, 'paidByEmail') || _.get(payment, 'metadata.playerEmail') || _.get(payment, 'user.email') || '';

  // Who the payment is FOR: the player (metadata.playerEmail), with the name
  // resolved server-side (paidForName). Falls back to the email; '' when there's
  // no player (team-pay).
  const paidForName = (payment) => _.get(payment, 'paidForName') || '';
  const paidForEmail = (payment) => _.get(payment, 'metadata.playerEmail') || '';

  const async_getPaymentsByLeagueName = async (leagueName) => {
    const token = await getToken();
    await getPaymentsByLeagueName(leagueName, token);
  };

  // A row is a refund document if its amount is negative. A payment can be refunded
  // (fully or partially) while it still has a REMAINING balance = amount paid minus
  // whatever has already been refunded; once nothing remains it's fully "Refunded".
  const refundedCents = (p) => _.sumBy(p?.refunds || [], (r) => r.amount || 0);
  const remainingRefundable = (p) => Math.max(0, (p?.amount ?? 0) - refundedCents(p)); // cents
  const isRefund = (p) => (p?.amount ?? 0) < 0;
  const isRefundable = (p) => (p?.amount ?? 0) > 0 && !!p?.stripe_payment_intent && remainingRefundable(p) > 0;
  const isFullyRefunded = (p) => (p?.amount ?? 0) > 0 && refundedCents(p) > 0 && remainingRefundable(p) === 0;

  // Open the refund dialog pre-filled with the full remaining amount (dollars) — so
  // confirming as-is is a full refund and editing it down is a partial refund.
  const openRefund = (payment) => {
    setConfirmTarget(payment);
    setRefundAmountInput((remainingRefundable(payment) / 100).toFixed(2));
  };

  // Cents parsed from the input, and whether they're a valid refund (0 < x <= remaining).
  const targetRemainingCents = confirmTarget ? remainingRefundable(confirmTarget) : 0;
  const parsedRefundCents = Math.round((parseFloat(refundAmountInput) || 0) * 100);
  const refundValid = !!confirmTarget && parsedRefundCents > 0 && parsedRefundCents <= targetRemainingCents;

  // Refund the entered amount (partial or full). Refetch afterwards so a partial
  // refund re-shows the row with a reduced remaining, or a full one flips it to
  // "Refunded" and the new negative refund row appears. Backend caps at the real
  // Stripe remaining, so a stale client value can never over-refund.
  const handleConfirmRefund = async () => {
    const p = confirmTarget;
    if (!p || !p.stripe_payment_intent || !refundValid) return;
    setRefundingPI(p.stripe_payment_intent);
    try {
      await refundPaymentByPaymentIntentId(p.stripe_payment_intent, parsedRefundCents, helpers.getObjId(p.organization));
      await async_getPaymentsByLeagueName(leagueName);
    } finally {
      setRefundingPI(null);
      setConfirmTarget(null);
      setRefundAmountInput('');
    }
  };

  useEffect(() => {
    async_getPaymentsByLeagueName(leagueName);
    getAllRegistrations(leagueName);
  }, []);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, registration, startDate, endDate]);

  const filteredPayments = payments.filter((payment) => {
    const email = payerEmail(payment).toLowerCase();
    const name = payerName(payment).toLowerCase();
    const rawTeamName = _.get(payment, 'metadata.teamName', '').toLowerCase();
    const displayTeamName = resolveTeamName(payment).toLowerCase();
    const paymentRegistration = _.get(payment, 'metadata.registration', '');
    const amount = _.get(payment, 'amount', 0);

    // Skip degenerate rows that carry no identifying info — likely stray webhook
    // entries with empty metadata. Otherwise they render as a blank row at the top.
    if (!email && !rawTeamName && !amount) return false;

    // Filter by search term (payer name, email, or team name — resolved player
    // names + raw).
    const term = searchTerm.toLowerCase();
    const forName = paidForName(payment).toLowerCase();
    const forEmail = paidForEmail(payment).toLowerCase();
    const matchesSearch = name.includes(term) || email.includes(term) || displayTeamName.includes(term) || rawTeamName.includes(term)
      || forName.includes(term) || forEmail.includes(term);
    
    // Filter by selected registration
    const matchesRegistration = !registration || paymentRegistration === registration;
    
    // Filter by date range
    let matchesDate = true;
    if (startDate || endDate) {
      const paidDate = new Date(_.get(payment, 'paidDate', ''));
      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        if (paidDate < start) matchesDate = false;
      }
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (paidDate > end) matchesDate = false;
      }
    }

    return matchesSearch && matchesRegistration && matchesDate;
  });

  const sortedRegistrations = _.orderBy(registrations, ['created', 'desc']);
  const sortedPayments = _.sortBy(filteredPayments, (payment) => {
    const paidDate = _.get(payment, 'paidDate', '');
    return new Date(paidDate).getTime() * -1;
  });

  // Pagination calculations
  const totalPayments = sortedPayments.length;
  const totalPages = Math.ceil(totalPayments / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedPayments = sortedPayments.slice(startIndex, endIndex);

  const handleSearch = () => {
    // startTransition(() => {
    //   fetchPayments(searchTerm, registration).then(setPayments)
    // })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (value: string) => {
    setRowsPerPage(Number(value));
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handleExportCSV = () => {
    if (!sortedPayments.length) return;

    const headers = ['Payer Name', 'Payer Email', 'Paid For', 'Team Name', 'Registration', 'Paid Date', 'Amount', 'Platform Fee'];
    const rows = sortedPayments.map((payment) => [
      payerName(payment),
      payerEmail(payment),
      paidForName(payment) || paidForEmail(payment) || '',
      resolveTeamName(payment),
      getRegistrationName(payment.registration),
      payment.paidDate ? new Date(payment.paidDate).toLocaleString() : '',
      payment.amount != null ? (Number(payment.amount) / 100).toFixed(2) : '',
      (Number(payment.applicationFeeAmount || 0) / 100).toFixed(2),
    ]);

    const escapeCSV = (value: any) => {
      const str = String(value ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csv = [headers, ...rows].map((row) => row.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transactions${startDate ? '_from_' + format(startDate, 'yyyy-MM-dd') : ''}${endDate ? '_to_' + format(endDate, 'yyyy-MM-dd') : ''}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex space-x-4 mb-4">
        <Input
          placeholder="Search by name, email or team name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        <Select value={registration} onValueChange={setRegistration}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select registration" />
          </SelectTrigger>
          <SelectContent>
            {_.map(sortedRegistrations, (registration) => (
              <SelectItem key={registration._id} value={registration._id}>{getRegistrationName(registration)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={startDate} onSelect={setStartDate} initialFocus />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}>
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(endDate, 'MMM dd, yyyy') : 'End date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={endDate} onSelect={setEndDate} disabled={(date) => startDate ? date < startDate : false} initialFocus />
          </PopoverContent>
        </Popover>
        {(startDate || endDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setStartDate(undefined); setEndDate(undefined); }}>
            Clear dates
          </Button>
        )}
        <Button onClick={handleSearch} disabled={isPending}>
          {isPending ? 'Filtering...' : 'Filter'}
        </Button>
        <Button variant="outline" onClick={handleExportCSV} disabled={!sortedPayments.length}>
          <Download className="mr-2 h-4 w-4" />
          Export
        </Button>
      </div>
      
      {totalPayments === 0 ? (
        payments.length === 0 ? (
          <EmptyState
            icon={CreditCard}
            title="No payments yet"
            description="Registration payments and donations will appear here once teams start paying. Set a registration fee to start collecting."
          />
        ) : (
          <EmptyState
            icon={CreditCard}
            title="No matching payments"
            description="No payments match the current search or filters. Try widening the date range or clearing the search."
          />
        )
      ) : (
      <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Payer Name</TableHead>
            <TableHead>Payer Email</TableHead>
            <TableHead>Paid For</TableHead>
            <TableHead>Team Name</TableHead>
            <TableHead>Registration</TableHead>
            <TableHead>Paid Date</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Platform Fee</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedPayments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>{getHighlightedText(payerName(payment), searchTerm)}</TableCell>
              <TableCell>{getHighlightedText(payerEmail(payment), searchTerm)}</TableCell>
              <TableCell>
                {paidForName(payment) ? (
                  <div>
                    <div>{getHighlightedText(paidForName(payment), searchTerm)}</div>
                    {paidForEmail(payment) && (
                      <div className="text-xs text-muted-foreground">{getHighlightedText(paidForEmail(payment), searchTerm)}</div>
                    )}
                  </div>
                ) : paidForEmail(payment) ? (
                  getHighlightedText(paidForEmail(payment), searchTerm)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell>{getHighlightedText(resolveTeamName(payment), searchTerm)}</TableCell>
              <TableCell>{getRegistrationName(payment.registration)}</TableCell>
              <TableCell>{new Date(payment.paidDate).toLocaleString()}</TableCell>
              <TableCell>{helpers.convertToUSD(payment.amount ?? 0)}</TableCell>
              <TableCell>
                {payment.applicationFeeAmount ? (
                  helpers.convertToUSD(payment.applicationFeeAmount)
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right">
                {isRefundable(payment) ? (
                  <div className="flex flex-col items-end gap-0.5">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={refundingPI === payment.stripe_payment_intent}
                      onClick={() => openRefund(payment)}
                    >
                      {refundingPI === payment.stripe_payment_intent ? 'Refunding…' : 'Refund'}
                    </Button>
                    {refundedCents(payment) > 0 && (
                      <span className="text-xs text-muted-foreground">({helpers.convertToUSD(refundedCents(payment))} refunded)</span>
                    )}
                  </div>
                ) : isFullyRefunded(payment) ? (
                  <span className="text-sm text-muted-foreground">Refunded</span>
                ) : isRefund(payment) ? (
                  <span className="text-sm text-muted-foreground">Refund</span>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-end mt-4 space-x-4">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-foreground">Rows per page:</span>
          <Select value={rowsPerPage.toString()} onValueChange={handleRowsPerPageChange}>
            <SelectTrigger className="w-[80px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-foreground">
            {startIndex + 1}-{Math.min(endIndex, totalPayments)} of {totalPayments} payments
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNumber}
                  variant={currentPage === pageNumber ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNumber)}
                  className="w-8 h-8 p-0"
                >
                  {pageNumber}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
      </>
      )}

      <AlertDialog open={!!confirmTarget} onOpenChange={(open) => { if (!open && !refundingPI) { setConfirmTarget(null); setRefundAmountInput(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Refund payment</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmTarget && (
                <>
                  Refund <b>{payerName(confirmTarget) || payerEmail(confirmTarget) || 'the payer'}</b>
                  {resolveTeamName(confirmTarget) ? <> for <b>{resolveTeamName(confirmTarget)}</b></> : null}.
                  {' '}Enter an amount up to the remaining balance. This cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmTarget && (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                Paid {helpers.convertToUSD(confirmTarget.amount ?? 0)}
                {refundedCents(confirmTarget) > 0 && <> · already refunded {helpers.convertToUSD(refundedCents(confirmTarget))}</>}
                {' '}· <b>Max {helpers.convertToUSD(targetRemainingCents)}</b>
              </div>
              <label className="text-sm font-medium">Refund amount ($)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                max={(targetRemainingCents / 100).toFixed(2)}
                value={refundAmountInput}
                onChange={(e) => setRefundAmountInput(e.target.value)}
                disabled={!!refundingPI}
              />
              {!refundValid && refundAmountInput !== '' && (
                <p className="text-xs text-destructive">
                  Enter an amount between $0.01 and {helpers.convertToUSD(targetRemainingCents)}.
                </p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!refundingPI}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmRefund(); }}
              disabled={!!refundingPI || !refundValid}
            >
              {refundingPI ? 'Refunding…' : `Refund ${refundValid ? helpers.convertToUSD(parsedRefundCents) : ''}`.trim()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
